import {
  buildProposal,
  findImportTargetField,
  type ImportColumnDecision,
  type ImportItemType,
  type ImportRowProposal,
  type ImportTargetField,
} from "@lafayette/import-mapping";
import type { StaffImportPreview, StaffImportRow } from "@lafayette/schemas";

// The review step shows each source row twice: the RAW cells as the file holds them, and the PROPOSAL
// the mapping makes from them. `buildProposal` is the function the server runs at submit, so what staff
// see here is what the server will store. The raw text is shown as it is (white space kept), so a
// trimmed cell is visible as a difference.

export type ReviewRow = Readonly<{
  /** The number of the row among the data rows (1 is the first row under the header). */
  rowNumber: number;
  /** The spreadsheet row number. `null` for a row stored before this was kept. */
  sourceRowNumber: number | null;
  rawCells: readonly string[];
  /** The proposal the server saved at submit. `null` before the mapping is submitted. */
  savedProposal: StaffImportRow["proposal"];
}>;

/** The data rows of a preview: the rows under the header row, blank rows left out. */
export function reviewRowsFromPreview(preview: StaffImportPreview): readonly ReviewRow[] {
  return preview.rows
    .filter((row) => row.sourceRowNumber > preview.headerRowNumber && !row.blank)
    .map((row, index) => ({
      rowNumber: index + 1,
      sourceRowNumber: row.sourceRowNumber,
      rawCells: row.cells,
      savedProposal: null,
    }));
}

export function reviewRowsFromRows(rows: readonly StaffImportRow[]): readonly ReviewRow[] {
  return rows.map((row) => ({
    rowNumber: row.rowNumber,
    sourceRowNumber: row.sourceRowNumber,
    rawCells: row.rawCells,
    savedProposal: row.proposal,
  }));
}

/** One mapped target field and the source columns that fill it, in column order. */
export type ReviewField = Readonly<{
  target: ImportTargetField;
  columnIndexes: readonly number[];
}>;

export function reviewFields(decisions: readonly ImportColumnDecision[]): readonly ReviewField[] {
  const byTarget = new Map<string, number[]>();
  for (const decision of [...decisions].sort((a, b) => a.columnIndex - b.columnIndex)) {
    if (decision.decision !== "MAPPED" || decision.targetField === null) continue;
    const list = byTarget.get(decision.targetField) ?? [];
    list.push(decision.columnIndex);
    byTarget.set(decision.targetField, list);
  }
  const fields: ReviewField[] = [];
  for (const [key, columnIndexes] of byTarget) {
    const target = findImportTargetField(key);
    if (target !== undefined) fields.push({ target, columnIndexes });
  }
  return fields;
}

/**
 * The proposal to show for a row. A submitted job shows what the server saved. A draft shows the
 * proposal the current mapping would make.
 */
export function proposalFor(
  row: ReviewRow,
  decisions: readonly ImportColumnDecision[],
  defaultItemType: ImportItemType | null,
  useSaved: boolean,
): ImportRowProposal {
  if (useSaved && row.savedProposal !== null) {
    return { itemType: row.savedProposal.itemType, fields: row.savedProposal.fields };
  }
  return buildProposal({ rawCells: row.rawCells, decisions, defaultItemType });
}

/** The raw text of the source columns of a field. */
export function rawValues(row: ReviewRow, field: ReviewField): readonly string[] {
  return field.columnIndexes.map((index) => row.rawCells[index] ?? "");
}

/** The proposed text of a field as a list (a single value is a list of one; no value is an empty list). */
export function proposedValues(proposal: ImportRowProposal, field: ReviewField): readonly string[] {
  const value = proposal.fields[field.target.key];
  if (value === undefined) return [];
  return typeof value === "string" ? [value] : value;
}
